import { TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import type { ClipboardEvent, KeyboardEvent } from 'react';

export interface NumericFieldProps extends Omit<TextFieldProps, 'type'> {
  /** Allow decimal point input (e.g. prices, coordinates). Default: false */
  allowDecimal?: boolean;
  /** Allow leading minus sign (e.g. discount percentages). Default: false */
  allowNegative?: boolean;
}

/**
 * Drop-in replacement for MUI TextField for numeric inputs.
 *
 * Uses `type="text"` + `inputMode="numeric"` to avoid browser quirks with
 * `type="number"` (mouse-scroll changes value, allows e/+/- chars, inconsistent
 * spinner UI). Restricts keypresses and paste to digits only (plus optional
 * decimal point and minus sign via props).
 */
export function NumericField({
  allowDecimal = false,
  allowNegative = false,
  onKeyDown,
  onPaste,
  inputProps,
  ...rest
}: NumericFieldProps) {
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (
      // Navigation / editing keys
      ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End',
        'ArrowLeft', 'ArrowRight'].includes(e.key) ||
      // Ctrl/Cmd shortcuts (select-all, copy, paste, cut, undo)
      ((e.ctrlKey || e.metaKey) &&
        ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) ||
      // Digits 0–9
      /^\d$/.test(e.key) ||
      // Optional decimal point
      (allowDecimal && e.key === '.') ||
      // Optional minus sign
      (allowNegative && e.key === '-')
    ) {
      onKeyDown?.(e);
      return;
    }
    e.preventDefault();
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const text = e.clipboardData.getData('text');
    const pattern = allowDecimal
      ? (allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/)
      : (allowNegative ? /^-?\d*$/ : /^\d*$/);
    if (!pattern.test(text.trim())) e.preventDefault();
    onPaste?.(e);
  };

  return (
    <TextField
      {...rest}
      type="text"
      inputProps={{
        inputMode: allowDecimal ? 'decimal' : 'numeric',
        ...inputProps,
      }}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
    />
  );
}
